const INITIALIZED_ATTR = 'data-swiper-initialized';

/**
 * Initializes a Swiper-based block on every matching element, then keeps
 * watching the DOM for new matches.
 *
 * With ACF blockVersion 3, Gutenberg re-renders a block's preview via AJAX
 * inside the editor iframe without re-running this script, so a plain
 * one-shot `new Swiper(selector)` misses any slider added or refreshed
 * after the initial load (new block, edited fields, etc.). Watching the
 * DOM lets the same script handle the frontend and the Gutenberg canvas.
 */
export function initSwiperOnMount(selector: string, createSwiper: (element: HTMLElement) => void) {
  const initElement = (element: Element) => {
    if (!(element instanceof HTMLElement) || element.hasAttribute(INITIALIZED_ATTR)) {
      return;
    }

    element.setAttribute(INITIALIZED_ATTR, 'true');
    createSwiper(element);
  };

  const initWithin = (root: ParentNode) => {
    root.querySelectorAll(selector).forEach(initElement);
  };

  initWithin(document);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches(selector)) {
          initElement(node);
        }

        initWithin(node);
      });
    });
  });

  // `document.documentElement` (unlike `document.body`) is available as
  // soon as parsing starts, which matters here: this script can run before
  // `document.body` exists (it is enqueued in the Gutenberg editor head via
  // `enqueue_block_editor_assets`), and `DOMContentLoaded` is not a
  // reliable enough signal inside the ACF v3 preview iframe.
  observer.observe(document.documentElement, { childList: true, subtree: true });
}
